/**
 * queryFunctions.js — Query function factories for React Query.
 *
 * IMPORTANT — Response shape contract:
 *   api.js does NOT unwrap Axios responses in its interceptor (interceptor
 *   returns `response`, not `response.data`). Every service call therefore
 *   returns a full Axios response { data, status, headers, … }.
 *   All query functions below call `.then(r => r.data)` to extract only the
 *   payload, which is what React Query stores and components consume directly.
 *
 * Each export is a *factory* — it takes parameters and returns the actual
 * async function that React Query calls. This keeps the queryKey and the
 * queryFn in sync without repetition.
 *
 * Existing service signatures (crmAPI.list, horseAPI.list, etc.) are never
 * modified here — we only wrap them.
 */

import api, {
  crmAPI,
  horseAPI,
  saleOpportunityAPI,
  saleAPI,
  saleCardAPI,
  associationAPI,
  userAPI,
  hospitalityAPI,
  marketingAPI,
  activityAPI,
  tenantAPI,
} from '../services/api';

// ── CRM Entities ─────────────────────────────────────────────────────────────

/** Returns Array<CrmEntity> */
export const fetchCrmEntities = (params = {}) =>
  () => crmAPI.list(params).then(r => r.data);

/** Returns CrmEntity */
export const fetchCrmEntity = (id) =>
  () => crmAPI.get(id).then(r => r.data);

/** Returns Array<AssociationRecord> */
export const fetchAssociations = (entityId) =>
  () => associationAPI.list(entityId).then(r => r.data);

/** Returns Array<ContactEntry> (email / phone / address rows) */
export const fetchContactEntries = (entityId) =>
  () => api.get(`/contact-entries?crm_entity_id=${entityId}`).then(r => r.data);

// ── Horses ────────────────────────────────────────────────────────────────────

/** Returns Array<Horse> */
export const fetchHorses = (params = {}) =>
  () => horseAPI.list(params).then(r => r.data);

/** Returns Horse */
export const fetchHorse = (id) =>
  () => horseAPI.get(id).then(r => r.data);

// ── Sale Opportunities ────────────────────────────────────────────────────────

/** Returns Array<SaleOpportunity> filtered by saleId or any params */
export const fetchSaleOpportunities = (params = {}) =>
  () => saleOpportunityAPI.list(params).then(r => r.data);

/** Returns SaleOpportunity */
export const fetchSaleOpportunity = (id) =>
  () => saleOpportunityAPI.get(id).then(r => r.data);

// ── Sale Cards ────────────────────────────────────────────────────────────────

/** Returns Array<SaleCard> (list view — no embedded viewings) */
export const fetchSaleCards = (saleId) =>
  () => saleCardAPI.list(saleId).then(r => r.data);

/** Returns SaleCard with embedded viewings */
export const fetchSaleCard = (cardId) =>
  () => api.get(`/sale-cards/${cardId}`).then(r => r.data);

/**
 * Returns Array<Viewing> — uses the batch /all-viewings endpoint.
 * One request per sale instead of one per card.
 */
export const fetchAllViewings = (saleId) =>
  () => api.get(`/sale-cards/all-viewings?sale_id=${saleId}`).then(r => r.data);

/** Returns ViewerSummary[] */
export const fetchViewerSummary = (saleId) =>
  () => api.get(`/sale-cards/viewer-summary?sale_id=${saleId}`).then(r => r.data);

// ── Vetting Procedures ────────────────────────────────────────────────────────

/** Returns Array<VettingProcedure> for a sale */
export const fetchVettingProcedures = (saleId) =>
  () => api.get(`/vetting-procedures?sale_id=${saleId}`).then(r => r.data);

// ── Users ─────────────────────────────────────────────────────────────────────

/** Returns Array<TenantUser> */
export const fetchTenantUsers = () =>
  () => userAPI.list().then(r => r.data);

// ── Tenant ────────────────────────────────────────────────────────────────────

/** Returns Tenant */
export const fetchTenant = (tenantId) =>
  () => tenantAPI.get(tenantId).then(r => r.data);

// ── Hospitality / Gifts ───────────────────────────────────────────────────────

/** Returns Array<HospitalityRecord> */
export const fetchHospitality = (params = {}) =>
  () => hospitalityAPI.list(params).then(r => r.data);

// ── Reports ───────────────────────────────────────────────────────────────────

/** Returns Array<SavedReport> */
export const fetchSavedReports = (params = {}) =>
  () => api.get('/saved-reports', { params }).then(r => r.data);

// ── Repository Sync ───────────────────────────────────────────────────────────

/** Returns { logs, total, page, page_size } */
export const fetchSyncLogs = (params = {}) =>
  () => api.get('/repository-sync/admin/logs', { params }).then(r => r.data);

// ── Sale Window ETA ───────────────────────────────────────────────────────────

/** Returns ETA estimates object — intended for short-lived polling queries */
export const fetchSaleWindowEstimates = (saleId) =>
  () => api.get(`/sale-window/estimates?sale_id=${saleId}`).then(r => r.data);


// ── Horse Detail Tabs ─────────────────────────────────────────────────────────

export const fetchRaceRecords = (horseId) =>
  () => api.get(`/race-class-records?horse_id=${horseId}`).then(r => r.data);

export const fetchProgenyAlerts = (horseId) =>
  () => api.get(`/race-class-records/progeny-alerts/${horseId}`).then(r => r.data);

export const fetchCoverRecords = (horseId) =>
  () => api.get(`/cover-records?horse_id=${horseId}`).then(r => r.data);

export const fetchOffspring = (horseId) =>
  () => api.get(`/horses/${horseId}/offspring`).then(r => r.data);

export const fetchOwnershipRecords = (params = {}) =>
  () => api.get('/ownership-records', { params }).then(r => r.data);

export const fetchSyndicateOwnership = (syndicateId) =>
  () => api.get(`/syndicate-ownership/${syndicateId}`).then(r => r.data);

export const fetchEntityFiles = (params = {}) =>
  () => api.get('/files', { params }).then(r => r.data);

// ── Shared / Tenant-scoped ────────────────────────────────────────────────────

export const fetchFarmfolioCatalog = () =>
  () => api.get('/farmfolio/care-catalog').then(r => r.data);

export const fetchFarmfolioLocations = () =>
  () => api.get('/farmfolio/locations').then(r => r.data);

export const fetchFarmfolioMetricsHorse = (horseId) =>
  () => api.get(`/farmfolio/metrics/horse/${horseId}`).then(r => r.data);

export const fetchFarmfolioCareLog = (horseId) =>
  () => api.get(`/farmfolio/care-log?horse_id=${horseId}`).then(r => r.data);

export const fetchFarmfolioScheduledCare = (horseId) =>
  () => api.get(`/farmfolio/scheduled-care?horse_id=${horseId}`).then(r => r.data);

export const fetchFarmfolioDueToday = (horseId) =>
  () => api.get(`/farmfolio/horse/${horseId}/due-today`).then(r => r.data);

export const fetchFarmfolioUpcoming = (horseId) =>
  () => api.get(`/farmfolio/horse/${horseId}/upcoming?days=30`).then(r => r.data);

export const fetchBillingEntities = () =>
  () => api.get('/billing-entities').then(r => r.data);

// ── Account Detail Tabs ───────────────────────────────────────────────────────

export const fetchAllInvoices = () =>
  () => api.get('/invoices').then(r => r.data);

export const fetchAllStatements = () =>
  () => api.get('/invoices/statements').then(r => r.data);

export const fetchAccountGrossSales = (entityId) =>
  () => api.get(`/reports/account-gross-sales?crm_entity_id=${entityId}`).then(r => r.data);

export const fetchFarmfolioMetricsAccount = (entityId) =>
  () => api.get(`/farmfolio/metrics/account/${entityId}`).then(r => r.data);

// ── Activities (Tasks / Communications) ─────────────────────────────────────

export const fetchActivities = (params = {}) =>
  () => activityAPI.list(params).then(r => r.data);

// ── Conformation / Inspection Notes ─────────────────────────────────────────

export const fetchConformationNotes = (horseId) =>
  () => api.get(`/conformation-notes?horse_id=${horseId}`).then(r => r.data);

// ── Marketing Campaigns ───────────────────────────────────────────────────────

export const fetchMarketing = (params = {}) =>
  () => marketingAPI.list(params).then(r => r.data);


// ── Calendar Events ───────────────────────────────────────────────────────────

export const fetchCalendarEvents = (startDate, endDate) =>
  () => activityAPI.getCalendarEvents(startDate, endDate).then(r => r.data);

export const fetchCalendarEventsAll = () =>
  () => activityAPI.getCalendarEvents('2020-01-01', '2030-12-31').then(r => r.data);

// ── Tasks ─────────────────────────────────────────────────────────────────────

export const fetchTasks = (scope) =>
  () => (scope === 'department'
    ? activityAPI.getDepartmentTasks()
    : activityAPI.getMyTasks()
  ).then(r => r.data);

// ── Dashboard ─────────────────────────────────────────────────────────────────

export const fetchTenantDashboard = () =>
  () => api.get('/dashboard/tenant').then(r => r.data);

// ── Settings (TenantAdminPage) ────────────────────────────────────────────────

export const fetchTenantAdminUsers = () =>
  () => api.get('/tenant-admin/users').then(r => r.data);

export const fetchTenantUserCapacity = () =>
  () => api.get('/tenant-admin/users/capacity').then(r => r.data);

export const fetchTenantDepartments = () =>
  () => api.get('/tenant-admin/departments').then(r => r.data);

export const fetchViewerAccess = () =>
  () => api.get('/crm-entities').then(r => (r.data || []).filter(e => e.owner_portal_access));

export const fetchStorageUsage = () =>
  () => api.get('/files/storage/usage').then(r => r.data);

// ── Reports (DSU saved reports via /reports/saved) ────────────────────────────

export const fetchReportsSaved = () =>
  () => api.get('/reports/saved').then(r => r.data);


// ── FarmFolio Global ──────────────────────────────────────────────────────────

export const fetchFarmfolioGlobal = () => async () => {
  const [hRes, dRes, uRes, lRes, cRes, locRes] = await Promise.all([
    api.get('/farmfolio/global/horses'),
    api.get('/farmfolio/global/due-today'),
    api.get('/farmfolio/global/upcoming?days=3650'),
    api.get('/farmfolio/global/care-log'),
    api.get('/farmfolio/care-catalog'),
    api.get('/farmfolio/locations'),
  ]);
  return {
    horses:    hRes.data   || [],
    dueToday:  dRes.data   || [],
    upcoming:  uRes.data   || [],
    careLog:   lRes.data   || [],
    catalog:   cRes.data   || [],
    locations: locRes.data || [],
  };
};

export const fetchFarmfolioGlobalScheduled = () =>
  () => api.get('/farmfolio/global/scheduled-care').then(r => r.data);

export const fetchFarmfolioLayups = () =>
  () => api.get('/farmfolio/layups').then(r => r.data);

// ── Invoice Module ────────────────────────────────────────────────────────────

export const fetchInvoiceDashboard = () =>
  () => api.get('/invoices/dashboard').then(r => r.data);

/** Fetch full invoice list — staleTime:0 caller ensures draft amounts are live. */
export const fetchInvoiceList = () =>
  () => api.get('/invoices').then(r => r.data);

/** Fetch single invoice by ID — backend live-reads draft commission + proceeds. */
export const fetchInvoice = (invoiceId) =>
  () => api.get(`/invoices/${invoiceId}`).then(r => r.data);

export const fetchInvoiceStandardItems = () =>
  () => api.get('/invoices/standard-items').then(r => r.data);

export const fetchRecurringInvoices = () =>
  () => api.get('/recurring-invoices').then(r => r.data);

export const fetchStatements = () =>
  () => api.get('/invoices/statements').then(r => r.data);

export const fetchStatement = (statementId) =>
  () => api.get(`/invoices/statements/${statementId}`).then(r => r.data);


// ── Sales Module ──────────────────────────────────────────────────────────────

export const fetchTenantSales = () =>
  () => api.get('/tenant-sales').then(r => r.data);

export const fetchSale = (saleId) =>
  () => api.get(`/sales/${saleId}`).then(r => r.data);

export const fetchSaleSessions = (saleId) =>
  () => api.get(`/sales/${saleId}/sessions`).then(r => r.data);

export const fetchSaleOpportunitiesForSale = (saleId) =>
  () => api.get(`/sale-opportunities?sale_id=${saleId}`).then(r => r.data);

export const fetchSaleDeliverables = (oppId) =>
  () => api.get(`/sale-deliverables?sale_opportunity_id=${oppId}`).then(r => r.data);

export const fetchSaleOwnerDeliverables = (saleId) =>
  () => api.get(`/sale-owner-deliverables?sale_id=${saleId}`).then(r => r.data);

export const fetchFunnelInsights = (saleId, activeOnly) =>
  () => api.get(`/sale-cards/funnel-insights?sale_id=${saleId}&active_only=${activeOnly}`).then(r => r.data);

export const fetchSaleOpportunityById = (id) =>
  () => saleOpportunityAPI.get(id).then(r => r.data);

// ── Owner Portal ──────────────────────────────────────────────────────────────

export const fetchOwnerPortalDashboard = () =>
  () => api.get('/owner-portal/dashboard').then(r => r.data);

export const fetchOwnerPortalAlerts = () =>
  () => api.get('/owner-portal/notifications').then(r => r.data);

export const fetchOwnerPortalBuyerAcks = () =>
  () => api.get('/owner-portal/buyer-acknowledgments').then(r => r.data);

export const fetchOwnerPortalQuestionnaires = () =>
  () => api.get('/owner-portal/questionnaires').then(r => r.data);

export const fetchOwnerPortalDocs = () =>
  () => api.get('/owner-portal/documents-to-sign').then(r => r.data);

export const fetchOwnerPortalReports = () =>
  () => api.get('/owner-portal/published-reports').then(r => r.data);

export const fetchOwnerPortalSyndicates = () =>
  () => api.get('/owner-portal/syndicates').then(r => r.data);

export const fetchOwnerPortalFinalReports = () =>
  () => api.get('/owner-portal/final-reports').then(r => r.data);

export const fetchOwnerPortalUnreadCount = () =>
  () => api.get('/messages/unread-count').then(r => r.data);


// ── Sale Opportunity Detail — shared queryFn ──────────────────────────────────
// Extracted so the same 2-stage parallel waterfall can be used for both
// the detail page and the hover-prefetch from the list page.

export const saleOpportunityDetailQueryFn = (id) => async () => {
  const oppRes = await saleOpportunityAPI.get(id);
  const opp = oppRes.data;

  const ownerIds = opp.owner_crm_entity_ids || [];
  const [
    horseResult, saleResult,
    sessResult, csResult, ownerDelsResult,
    breezeResult, dmResult,
    ...ownerResults
  ] = await Promise.allSettled([
    opp.horse_id ? horseAPI.get(opp.horse_id) : Promise.resolve({ data: null }),
    opp.sale_id  ? saleAPI.get(opp.sale_id)   : Promise.resolve({ data: null }),
    opp.sale_id  ? api.get(`/sales/${opp.sale_id}/sessions`) : Promise.resolve({ data: [] }),
    api.get(`/sale-opportunities/${id}/confirmation-status`),
    opp.sale_id  ? api.get(`/sale-owner-deliverables?sale_id=${opp.sale_id}`) : Promise.resolve({ data: [] }),
    api.get(`/breeze-times/${id}`),
    api.get(`/digital-metrics/${id}`),
    ...ownerIds.map(ownerId =>
      crmAPI.get(ownerId).catch(() =>
        api.get(`/tenants/${ownerId}`)
          .then(t => t.data?.id
            ? { data: { id: ownerId, system_name: t.data.name + ' (Consignor)', is_tenant_owner: true } }
            : { data: null })
          .catch(() => ({ data: null }))
      )
    ),
  ]);

  return {
    opportunity: opp,
    horse:    horseResult.status    === 'fulfilled' ? horseResult.value?.data    : null,
    sale:     saleResult.status     === 'fulfilled' ? saleResult.value?.data     : null,
    sessions: sessResult.status     === 'fulfilled' ? (Array.isArray(sessResult.value?.data) ? sessResult.value.data : []) : [],
    confirmationStatus:    csResult.status        === 'fulfilled' ? csResult.value?.data                      : null,
    saleOwnerDeliverables: ownerDelsResult.status === 'fulfilled' ? (ownerDelsResult.value?.data || [])       : [],
    breezeTime:    breezeResult.status === 'fulfilled' ? (breezeResult.value?.data || {}) : {},
    digitalMetrics: dmResult.status   === 'fulfilled' ? (dmResult.value?.data    || {}) : {},
    owners: ownerResults.map(r => r.status === 'fulfilled' ? r.value?.data : null).filter(Boolean),
  };
};


// ── ActiveSalesPage Tier-2 Enrichment ─────────────────────────────────────────
// Bundles all per-sale data (opps + horses + owners + deliverables) into one
// queryFn. Each active sale gets its own cache entry under QK.activeSaleEnrichment.

export const fetchSaleEnrichment = (saleId) => async () => {
  const oppsRes = await api.get(`/sale-opportunities?sale_id=${saleId}`);
  const opps = oppsRes.data || [];

  const uniqueOwnerIds = [...new Set(opps.flatMap(o => o.owner_crm_entity_ids || []))];

  const [horseResults, ownerResults, deliverableResults, ownerDelsRes] = await Promise.all([
    Promise.allSettled(
      opps.filter(o => o.horse_id).map(async o => {
        const r = await api.get(`/horses/${o.horse_id}`).catch(() => null);
        return { oppId: o.id, horse: r?.data };
      })
    ),
    Promise.allSettled(
      uniqueOwnerIds.map(async (ownerId) => {
        try {
          const r = await api.get(`/crm-entities/${ownerId}`);
          return { id: ownerId, data: r.data };
        } catch {
          try {
            const t = await api.get(`/tenants/${ownerId}`);
            if (t.data?.name) return {
              id: ownerId,
              data: { id: ownerId, system_name: t.data.name + ' (Consignor)', is_tenant_owner: true },
            };
          } catch { /* ignore */ }
          return null;
        }
      })
    ),
    Promise.allSettled(
      opps.map(o => api.get(`/sale-deliverables?sale_opportunity_id=${o.id}`).catch(() => ({ data: [] })))
    ),
    api.get(`/sale-owner-deliverables?sale_id=${saleId}`).catch(() => ({ data: [] })),
  ]);

  // Horse lookup by opp id
  const horseByOppId = {};
  for (const r of horseResults) {
    if (r.status === 'fulfilled' && r.value?.horse) horseByOppId[r.value.oppId] = r.value.horse;
  }

  // Deliverables by opp id
  const deliverablesByOppId = {};
  opps.forEach((o, i) => {
    deliverablesByOppId[o.id] = deliverableResults[i]?.status === 'fulfilled'
      ? (deliverableResults[i].value.data || [])
      : [];
  });

  // Owner entities + per-opp owner name strings
  const ownerEntitiesMap = {};
  for (const r of ownerResults) {
    if (r.status === 'fulfilled' && r.value) ownerEntitiesMap[r.value.id] = r.value.data;
  }
  const opportunityOwners = {};
  for (const opp of opps) {
    if (opp.owner_crm_entity_ids?.length > 0) {
      const names = opp.owner_crm_entity_ids
        .map(id => ownerEntitiesMap[id]?.system_name)
        .filter(Boolean);
      if (names.length > 0) opportunityOwners[opp.id] = names.join(', ');
    }
  }

  return {
    opportunities: opps.map(o => {
      const horse = horseByOppId[o.id];
      return {
        ...o,
        deliverables: deliverablesByOppId[o.id] ?? [],
        dam_name:   horse?.dam_name  || '',
        sire_name:  horse?.sire_name || '',
        horse_display_name: horse?.system_name || horse?.name
          || o.opportunity_name?.split(' - ').slice(1).join(' - ') || '',
      };
    }),
    saleOwnerDeliverables: ownerDelsRes.data || [],
    opportunityOwners,
    ownerEntities: ownerEntitiesMap,
  };
};

